import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { GlobalFileDropOverlay } from "@/features/globalUpload/GlobalFileDropOverlay";
import { GlobalUploadDialog } from "@/features/globalUpload/GlobalUploadDialog";
import type { GlobalUploadFileEntry } from "@/features/globalUpload/globalUploadConstants";
import {
  GLOBAL_UPLOAD_OVER_OVERVIEW_OVERLAY_Z,
  GLOBAL_UPLOAD_OVER_OVERVIEW_Z,
  GLOBAL_UPLOAD_OVERLAY_Z,
  GLOBAL_UPLOAD_Z,
} from "@/features/globalUpload/globalUploadLayers";
import { useGlobalFileDrop } from "@/features/globalUpload/useGlobalFileDrop";
import { useCurrentUser, userIsBuero, userIsMonteure } from "@/lib/auth";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";

function orderIdFromProjectPath(pathname: string): number | null {
  const match = pathname.match(/\/projekte\/(\d+)\/?$/);
  const raw = match?.[1];
  if (!raw) return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function createFileEntry(file: File): GlobalUploadFileEntry {
  return {
    id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
    original: file,
    customName: file.name,
    hasConflict: false,
    asSchadensmeldung: false,
  };
}

function fileListToEntries(fileList: FileList | File[]): GlobalUploadFileEntry[] {
  return Array.from(fileList).map(createFileEntry);
}

export function GlobalUploadProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const location = useLocation();
  const { isOpen: overviewOpen, activeId: overviewAuftragId } = useProjectOverview();
  const canUpload = useMemo(
    () => userIsBuero(user) || userIsMonteure(user),
    [user],
  );

  const [overlayActive, setOverlayActive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [files, setFiles] = useState<GlobalUploadFileEntry[]>([]);

  const projectPageOrderId = orderIdFromProjectPath(location.pathname);
  const preselectedAuftragId =
    overviewOpen && overviewAuftragId != null && overviewAuftragId > 0
      ? overviewAuftragId
      : projectPageOrderId;

  const dialogZIndexClass = overviewOpen ? GLOBAL_UPLOAD_OVER_OVERVIEW_Z : GLOBAL_UPLOAD_Z;
  const overlayZIndexClass = overviewOpen
    ? GLOBAL_UPLOAD_OVER_OVERVIEW_OVERLAY_Z
    : GLOBAL_UPLOAD_OVERLAY_Z;

  const addFiles = useCallback((fileList: FileList | File[]) => {
    const next = fileListToEntries(fileList);
    if (!next.length) return;
    setFiles((prev) => [...prev, ...next]);
    setDialogOpen(true);
  }, []);

  const handleDrop = useCallback(
    (fileList: FileList) => {
      addFiles(fileList);
    },
    [addFiles],
  );

  useGlobalFileDrop({
    enabled: canUpload,
    onDrop: handleDrop,
    onDragActiveChange: setOverlayActive,
  });

  useEffect(() => {
    setOverlayActive(false);
  }, [location.pathname, location.search, location.hash]);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
    setFiles([]);
  }, []);

  return (
    <>
      {children}
      {canUpload ? (
        <>
          <GlobalFileDropOverlay
            active={overlayActive && !dialogOpen}
            zIndexClass={overlayZIndexClass}
          />
          <GlobalUploadDialog
            open={dialogOpen}
            onClose={handleClose}
            files={files}
            onFilesChange={setFiles}
            onAddFiles={addFiles}
            preselectedAuftragId={preselectedAuftragId}
            zIndexClass={dialogZIndexClass}
          />
        </>
      ) : null}
    </>
  );
}
